import { Navigate, Route } from 'react-router-dom';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import RequireSection from '@/components/auth/RequireSection';
import { ROUTE_MANIFEST, type SectionRoute } from './manifest';

function renderRoute(route: SectionRoute) {
  if ('redirectTo' in route) {
    return <Route key={route.path} path={route.path} element={<Navigate to={route.redirectTo} replace />} />;
  }
  const Page = route.page;
  return <Route key={route.path} path={route.path} element={<Page />} />;
}

/**
 * The signed-in app shell: every manifest route, each inside a RequireSection
 * for its own section, so the URL is gated by the same permission that shows
 * or hides its sidebar link. There is no way to add an app route that skips
 * the guard short of editing this function.
 *
 * Routes carrying `allowSelfParam` get their own guard group, since the self
 * exemption is a prop of the guard rather than of the route.
 */
export function renderProtectedRoutes() {
  return (
    <Route element={<ProtectedRoute />}>
      {ROUTE_MANIFEST.flatMap(({ section, routes }) => {
        const groups = new Map<string | undefined, SectionRoute[]>();
        for (const route of routes) {
          const selfParam = 'page' in route ? route.allowSelfParam : undefined;
          groups.set(selfParam, [...(groups.get(selfParam) ?? []), route]);
        }
        return [...groups].map(([selfParam, groupRoutes]) => (
          <Route
            key={`${section}:${selfParam ?? ''}`}
            element={<RequireSection section={section} allowSelfParam={selfParam} />}
          >
            {groupRoutes.map(renderRoute)}
          </Route>
        ));
      })}
    </Route>
  );
}
